import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  FileText as FileTextIcon,
  Filter,
  LayoutGrid,
  List,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Clock,
  User,
  MessageSquare,
} from 'lucide-react'
import { useContentTasks } from '../hooks/useContentTasks'
import { usePersonas } from '../hooks/usePersonas'
import { useAppStore } from '../stores/app'
import { supabase } from '../lib/supabase'
import { listConversations, getTaskDraft, getConversation, updateConversation, ApiError } from '../lib/api'
import type { TaskDraftResponse } from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { ContentTaskForm, ContentTaskFormData } from '../components/content/ContentTaskForm'
import { TaskCard } from '../components/content/TaskCard'
import { TaskDraftReviewModal } from '../components/content/TaskDraftReviewModal'
import { ConversationSourceControls } from '../components/content/ConversationSourceControls'
import { EmptyState } from '../components/ui/EmptyState'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { QueryErrorState } from '../components/ui/QueryErrorState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Modal } from '../components/ui/Modal'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { toast } from 'sonner'

const ITEMS_PER_PAGE = 12

type ViewMode = 'cards' | 'table'
type SortField = 'created_at' | 'topic' | 'status' | 'content_type'
type SortDir = 'asc' | 'desc'

export function ContentPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const organizationId = useAppStore((state) => state.organizationId)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isConversationPickerOpen, setIsConversationPickerOpen] = useState(false)
  const [draftFromConversation, setDraftFromConversation] = useState<TaskDraftResponse | null>(null)
  const [loadingDraft, setLoadingDraft] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [personaFilter, setPersonaFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('cards')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const filters = {
    ...(statusFilter && { status: statusFilter }),
    ...(personaFilter && { persona_id: personaFilter }),
  }

  const {
    data: tasks,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
    createTask,
    cancelTask,
    rerunTask,
    isCreating,
  } = useContentTasks(filters)
  const { data: personas } = usePersonas()
  const { data: conversations = [], isLoading: loadingConversations } = useQuery({
    queryKey: ['chat_conversations'],
    queryFn: () => listConversations(20, 0),
    enabled: isConversationPickerOpen,
  })

  const { data: selectedConversation } = useQuery({
    queryKey: ['chat_conversation', selectedConversationId],
    queryFn: () => getConversation(selectedConversationId!),
    enabled: isConversationPickerOpen && !!selectedConversationId,
  })

  const handleCreate = async (data: ContentTaskFormData) => {
    await createTask(data)
    setIsFormOpen(false)
  }

  const handleConfirmDraft = async (payload: ContentTaskFormData) => {
    await createTask(payload)
    setDraftFromConversation(null)
  }

  const handleFetchDraft = async () => {
    if (!selectedConversationId || !organizationId) return
    setLoadingDraft(true)
    try {
      const draft = await getTaskDraft(selectedConversationId)
      setDraftFromConversation(draft)
      setIsConversationPickerOpen(false)
      setSelectedConversationId(null)
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error('Error al obtener borrador', { description: err.message })
      } else {
        toast.error('Error al obtener borrador', { description: getErrorMessage(err) })
      }
    } finally {
      setLoadingDraft(false)
    }
  }

  const handleSaveSourceControls = async (activeSources: string[]) => {
    if (!selectedConversationId) return
    try {
      await updateConversation(selectedConversationId, { active_sources: activeSources })
      await queryClient.invalidateQueries({ queryKey: ['chat_conversations'] })
      await queryClient.invalidateQueries({ queryKey: ['chat_conversation', selectedConversationId] })
      toast.success('Fuentes guardadas', { description: 'La configuración de fuentes se actualizó correctamente.' })
    } catch (err) {
      toast.error('Error al guardar', { description: getErrorMessage(err) })
    }
  }

  const handleCancel = async (taskId: string) => {
    if (window.confirm('¿Estás seguro de que quieres cancelar esta tarea?')) {
      await cancelTask(taskId)
    }
  }

  const handleRerun = async (taskId: string) => {
    if (window.confirm('¿Quieres reintentar esta tarea?')) {
      await rerunTask(taskId)
    }
  }

  const getPersonaName = (personaId: string) => {
    return personas?.find((p) => p.id === personaId)?.name || 'Desconocido'
  }

  // Filter by search query
  const taskList = Array.isArray(tasks) ? tasks : []
  const filteredTasks = useMemo(() => {
    if (!taskList || taskList.length === 0) return []
    if (!searchQuery.trim()) return taskList

    const query = searchQuery.toLowerCase()
    return taskList.filter(
      (task: { topic: string; brief: string | null; content_type: string; channel: string }) =>
        task.topic.toLowerCase().includes(query) ||
        (task.brief && task.brief.toLowerCase().includes(query)) ||
        task.content_type.toLowerCase().includes(query) ||
        task.channel.toLowerCase().includes(query)
    )
  }, [taskList, searchQuery])

  // Sort
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks]
    sorted.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'topic':
          cmp = a.topic.localeCompare(b.topic)
          break
        case 'status':
          cmp = a.status.localeCompare(b.status)
          break
        case 'content_type':
          cmp = a.content_type.localeCompare(b.content_type)
          break
        case 'created_at':
        default:
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [filteredTasks, sortField, sortDir])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / ITEMS_PER_PAGE))
  const paginatedTasks = sortedTasks.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const taskIds = useMemo(() => paginatedTasks.map((t: { id: string }) => t.id), [paginatedTasks])
  const { data: piecesForTasks } = useQuery({
    queryKey: ['content_pieces_sources', taskIds],
    queryFn: async () => {
      if (taskIds.length === 0) return []
      const { data, error } = await supabase
        .from('content_pieces')
        .select('task_id, sources')
        .in('task_id', taskIds)
      if (error) throw error
      return data as { task_id: string; sources?: unknown[] }[]
    },
    enabled: taskIds.length > 0,
  })

  const sourcesCountByTask = useMemo(() => {
    const map: Record<string, number> = {}
    for (const row of piecesForTasks ?? []) {
      const count = Array.isArray(row.sources) ? row.sources.length : 0
      map[row.task_id] = (map[row.task_id] ?? 0) + count
    }
    return map
  }, [piecesForTasks])

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const hasFilters = statusFilter || personaFilter || searchQuery

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contenido</h1>
          <p className="text-slate-500 mt-1">
            Genera y gestiona contenido
            {filteredTasks.length > 0 && (
              <span className="ml-2 text-slate-400">
                ({filteredTasks.length} tarea{filteredTasks.length !== 1 ? 's' : ''})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFormOpen(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nueva Tarea
          </button>
          <button
            onClick={() => setIsConversationPickerOpen(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <MessageSquare className="w-5 h-5" />
            Crear desde conversación
          </button>
        </div>
      </div>

      {/* Filters + Search + View toggle */}
      <div className="card mb-6">
        <div className="flex flex-col gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por tema, descripción, tipo o canal..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setCurrentPage(1)
              }}
              className="input pl-10"
            />
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>

            <select
              className="input w-44"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="running">En proceso</option>
              <option value="completed">Completado</option>
              <option value="failed">Fallido</option>
              <option value="cancelled">Cancelado</option>
            </select>

            <select
              className="input w-44"
              value={personaFilter}
              onChange={(e) => {
                setPersonaFilter(e.target.value)
                setCurrentPage(1)
              }}
            >
              <option value="">Todas las personas</option>
              {personas?.map((persona) => (
                <option key={persona.id} value={persona.id}>
                  {persona.name}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                onClick={() => {
                  setStatusFilter('')
                  setPersonaFilter('')
                  setSearchQuery('')
                  setCurrentPage(1)
                }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Limpiar filtros
              </button>
            )}

            {/* View mode toggle */}
            <div className="ml-auto flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                title="Vista de cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                title="Vista de tabla"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Task list */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : isError && error ? (
        <QueryErrorState
          title="Error al cargar las tareas"
          error={error}
          onRetry={() => refetch()}
          isRefetching={isRefetching}
        />
      ) : paginatedTasks.length > 0 ? (
        <>
          {viewMode === 'cards' ? (
            /* Card view */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  personaName={getPersonaName(task.persona_id)}
                  sourcesCount={sourcesCountByTask[task.id] ?? 0}
                  onClick={() => navigate(`/content/${task.id}`)}
                  onCancel={() => handleCancel(task.id)}
                  onRerun={() => handleRerun(task.id)}
                />
              ))}
            </div>
          ) : (
            /* Table view */
            <div className="card p-0 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left">
                      <button
                        onClick={() => toggleSort('topic')}
                        className="flex items-center gap-1 px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700"
                      >
                        Tema
                        <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Persona
                    </th>
                    <th className="text-left">
                      <button
                        onClick={() => toggleSort('content_type')}
                        className="flex items-center gap-1 px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700"
                      >
                        Tipo
                        <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Canal
                    </th>
                    <th className="text-left">
                      <button
                        onClick={() => toggleSort('status')}
                        className="flex items-center gap-1 px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700"
                      >
                        Estado
                        <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-left">
                      <button
                        onClick={() => toggleSort('created_at')}
                        className="flex items-center gap-1 px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700"
                      >
                        Fecha
                        <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedTasks.map((task) => (
                    <tr
                      key={task.id}
                      onClick={() => navigate(`/content/${task.id}`)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate max-w-[250px]">
                            {task.topic}
                          </p>
                          {task.brief && (
                            <p className="text-xs text-slate-400 truncate max-w-[250px]">
                              {task.brief}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {getPersonaName(task.persona_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <FileTextIcon className="w-3 h-3" />
                          {task.content_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                          {task.channel}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={task.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(task.created_at), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(task.status === 'failed' || task.status === 'cancelled') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRerun(task.id)
                              }}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              Reintentar
                            </button>
                          )}
                          {(task.status === 'pending' || task.status === 'running') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCancel(task.id)
                              }}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-slate-500">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                {Math.min(currentPage * ITEMS_PER_PAGE, sortedTasks.length)} de{' '}
                {sortedTasks.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-emerald-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card">
          <EmptyState
            icon={<FileTextIcon className="w-16 h-16" />}
            title={hasFilters ? 'No se encontraron tareas' : 'No hay tareas de contenido'}
            description={
              hasFilters
                ? 'Intenta cambiar los filtros o la búsqueda para ver más resultados'
                : 'Crea tu primera tarea para comenzar a generar contenido con IA'
            }
            action={
              !hasFilters
                ? {
                    label: 'Crear primera tarea',
                    onClick: () => setIsFormOpen(true),
                  }
                : undefined
            }
          />
        </div>
      )}

      <ContentTaskForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
      />

      <Modal
        isOpen={isConversationPickerOpen}
        onClose={() => {
          setIsConversationPickerOpen(false)
          setSelectedConversationId(null)
        }}
        title="Crear tarea desde conversación"
        size="md"
      >
        <p className="text-sm text-slate-600 mb-4">
          Elige una conversación para generar un borrador de tarea. Luego podrás revisarlo y crear la tarea.
        </p>
        {loadingConversations ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : conversations.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No hay conversaciones. Crea una en el chat.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                type="button"
                onClick={() => setSelectedConversationId(conv.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedConversationId === conv.id
                    ? 'border-emerald-500 bg-emerald-50 text-slate-800'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <span className="font-medium block truncate">
                  {conv.title || 'Sin título'}
                </span>
                <span className="text-xs text-slate-500">
                  {formatDistanceToNow(new Date(conv.created_at), { addSuffix: true, locale: es })}
                </span>
              </button>
            ))}
          </div>
        )}
        {selectedConversationId && (
          <div className="mt-4">
            <ConversationSourceControls
              conversationId={selectedConversationId}
              initialActiveSources={
                selectedConversation?.active_sources && selectedConversation.active_sources.length > 0
                  ? selectedConversation.active_sources
                  : undefined
              }
              onSave={handleSaveSourceControls}
            />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => {
              setIsConversationPickerOpen(false)
              setSelectedConversationId(null)
            }}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleFetchDraft}
            disabled={!selectedConversationId || loadingDraft}
            className="btn btn-primary"
          >
            {loadingDraft ? 'Obteniendo borrador...' : 'Obtener borrador'}
          </button>
        </div>
      </Modal>

      <TaskDraftReviewModal
        isOpen={draftFromConversation !== null}
        onClose={() => setDraftFromConversation(null)}
        draft={draftFromConversation}
        onCreateTask={handleConfirmDraft}
        isLoading={isCreating}
      />
    </div>
  )
}
